import { type ReactElement } from 'react';
import clsx from 'clsx';
import { TdsIcon, TdsTooltip } from '@scania/tegel-react';
import { useId } from 'react';
import { NotAvailable } from '../../../NotAvailable';
import { lifetimeDailyTooltip } from '../../../operational-tooltips';
import styles from './style.module.scss';

interface OdometerProperties {
  readonly odometerTotal?: number;
  readonly odometerDaily?: number;
  readonly operationalFromDate?: string | null;
  readonly operationalToDate?: string | null;
}

const Odometer = ({
  odometerTotal,
  odometerDaily,
  operationalFromDate,
  operationalToDate,
}: OdometerProperties): ReactElement => {
  const tooltipId = `daily-distance-${useId().replaceAll(':', '')}`;
  const tooltip = lifetimeDailyTooltip(
    '75th percentile (Q3) of daily distance',
    operationalFromDate,
    operationalToDate,
  );
  const hasTotal = odometerTotal !== undefined;
  const hasDaily = odometerDaily !== undefined;

  const displayTotal = hasTotal ? `${odometerTotal.toLocaleString()} km` : <NotAvailable />;
  const dailyText = hasDaily ? `${odometerDaily.toFixed(0)} km` : 'N/A';
  const displayDaily = hasDaily ? dailyText : <NotAvailable />;

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <TdsIcon name="speedometer" size="24px" />
        <h3 className={styles.title}>Odometer</h3>
      </div>
      <div className={styles.footer}>
        <div className={clsx(styles.item, { [styles['not-available']]: !hasTotal })}>
          <span className={styles.label}>Total</span>
          <span className={styles.value}>{displayTotal}</span>
        </div>
        <div className={clsx(styles.item, { [styles['not-available']]: !hasDaily })}>
          <span className={styles.label}>Daily</span>
          <button
            type="button"
            id={tooltipId}
            className={styles['daily-value']}
            aria-label={`${dailyText}. ${tooltip}`}
          >
            {displayDaily}
          </button>
          <TdsTooltip selector={`#${tooltipId}`} text={tooltip} placement="top" />
        </div>
      </div>
    </section>
  );
};

export default Odometer;
